/**
 * Image size formats available for IGDB images
 */
export type IgdbImageSize =
  | 'cover_small'
  | 'cover_small_2x'
  | 'screenshot_med'
  | 'cover_big'
  | 'cover_big_2x'
  | 'logo_med'
  | 'screenshot_big'
  | 'screenshot_big_2x'
  | 'screenshot_huge'
  | 'screenshot_huge_2x'
  | 'thumb'
  | 'thumb_2x'
  | 'micro'
  | '720p'
  | '720p_2x'
  | '1080p'
  | '1080p_2x'
  | 'artwork_big';

/**
 * Customizes an IGDB image URL to use a different size format
 *
 * @param url The original IGDB image URL
 * @param size The desired image size format
 * @returns The customized URL with the new size format
 */
export function customizeIgdbImageUrl(url: string, size: IgdbImageSize, format: 'jpg' | 'png' | 'webp'): string {
  if (!url || !url.includes('images.igdb.com')) {
    return url; // Return original URL if not an IGDB image
  }

  // Extract the image ID from the URL
  // Example URL: https://images.igdb.com/igdb/image/upload/t_thumb/abc123.jpg
  const match = url.match(/\/t_[^/]+\/([^.]+)\./);

  if (!match || !match[1]) {
    return url; // Return original URL if we can't extract the image ID
  }

  const imageId = match[1];

  // Construct the new URL with the desired size
  return `https://images.igdb.com/igdb/image/upload/t_${size}/${imageId}.${format}`;
}

/**
 * Gets the best image URL for display based on the provider and available sizes
 *
 * @param url The original image URL
 * @param providerId The provider ID (e.g., 'igdb')
 * @param preferredSize The preferred image size (default: 'cover_big')
 * @returns The optimized image URL
 */
export function getOptimizedImageUrl(
  url: string,
  providerId?: string,
  preferredSize: IgdbImageSize = '1080p',
  preferredFormat: 'jpg' | 'png' | 'webp' = 'webp',
): string {
  if (!url) {
    return '';
  }

  // Customize IGDB images
  if (providerId === 'igdb') {
    return customizeIgdbImageUrl(url, preferredSize, preferredFormat);
  }

  // Return original URL for other providers
  return url;
}

export function getOptimizedFullsizeImageUrl(url: string, providerId: string, height: number): string {
  let preset = '1080p';
  if (height <= 720) {
    preset = '720p';
  } else if (height <= 1080) {
    preset = '1080p';
  } else {
    preset = '1080p_2x';
  }
  return getOptimizedImageUrl(url, providerId, preset as IgdbImageSize, 'webp');
}
